/**
 * Reading a pasted grid — TECHNICAL_DESIGN.md §7.1.
 *
 * The paste boxes used to split on `,` and on newlines directly:
 *
 * ```
 * text.split(/\r?\n/).map((line) => line.split(','))
 * ```
 *
 * with a comment saying a quoted comma "is the one case this gets wrong", that
 * reconciliation would catch it, and that a real parser would be a dependency
 * for a paste box. All three were wrong.
 *
 * **Reconciliation cannot catch it.** §7.1's second keying compares two pastes
 * of the same text through the *same* parser. Both mis-split identically, the
 * comparison finds no divergence, and the control that exists to catch exactly
 * this kind of shift reports clean. A defect invisible to the check meant to see
 * it is worse than one nobody looked for.
 *
 * **The damage is not the one cell.** A mandate cell reads `max 20% (max 5% HY),
 * subject to paragraph 7`. Split naively it becomes two cells, and every column
 * after it on that row shifts one to the left — so the fund in the next column
 * gets a limit that was never written for it, with a cell reference and a
 * citation that look entirely correct. That is the failure this whole product
 * exists to prevent, arriving through the front door.
 *
 * **And it is not a dependency**, it is a state machine. Quoting is the one
 * thing every producer of these files agrees on: Excel, Numbers, Google Sheets
 * and every `csv` writer quote a field containing the delimiter, and double the
 * quote inside it.
 *
 * Newlines inside a quoted field are handled for the same reason — a
 * multi-line narrative cell pasted out of a spreadsheet would otherwise become
 * two rows, one of them nonsense.
 */

/** Tab where the paste came from a spreadsheet, comma where it came from a file.
 *  Decided over the whole text and outside quotes, because a tab-separated grid
 *  may legitimately hold commas and a comma-separated one may hold tabs. */
function delimiterOf(text: string): ',' | '\t' {
  let quoted = false
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i]
    if (ch === '"') {
      if (quoted && text[i + 1] === '"') {
        i += 1
        continue
      }
      quoted = !quoted
      continue
    }
    if (!quoted && ch === '\t') return '\t'
  }
  return ','
}

/**
 * A pasted grid as rows of cells.
 *
 * Blank rows are dropped — a trailing newline is not a row of empty limits.
 * Unquoted cells are trimmed, because a workbook column is full of stray
 * spacing; quoted ones are not, because the quotes say the content is
 * deliberate and trimming inside them would be editing the client's document.
 */
export function parseDelimited(text: string): string[][] {
  const delimiter = delimiterOf(text)
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let quoted = false
  let wasQuoted = false

  function endField() {
    row.push(wasQuoted ? field : field.trim())
    field = ''
    wasQuoted = false
  }

  function endRow() {
    endField()
    if (row.some((cell) => cell !== '')) rows.push(row)
    row = []
  }

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i]!

    if (quoted) {
      if (ch === '"') {
        // A doubled quote is one literal quote; a single one closes the field.
        if (text[i + 1] === '"') {
          field += '"'
          i += 1
        } else {
          quoted = false
        }
        continue
      }
      field += ch
      continue
    }

    // A quote opens a field only at its start. One appearing mid-cell — `5"
    // pipe` — is data, and treating it as a delimiter would corrupt the cell
    // this function exists to keep intact.
    if (ch === '"' && field.trim() === '') {
      quoted = true
      wasQuoted = true
      field = ''
      continue
    }
    if (ch === delimiter) {
      endField()
      continue
    }
    if (ch === '\r') continue
    if (ch === '\n') {
      endRow()
      continue
    }
    field += ch
  }

  endRow()
  return rows
}

/**
 * Rows back to text, quoted so that `parseDelimited` returns what it was given.
 *
 * The import fills its paste box from the uploaded sheet, so the server's
 * reading round-trips through a textarea before every later step re-reads it.
 * That join used to be `row.join(',')` — which is correct for every cell that
 * holds no comma, and silently destroys the ones that do. The server had read
 * `"Yes, monthly"` correctly out of the file; the client wrote it back as
 * `Yes, monthly`, and the next parse split it in two and shifted every column
 * after it. A parser that handles quoting cannot save a string the quoting has
 * already been stripped from.
 *
 * Quoted exactly when it has to be, so the box stays readable: a cell showing
 * `max 15%` should not show `"max 15%"` for the sake of the three cells that
 * need the quotes.
 */
export function formatDelimited(rows: string[][], delimiter: ',' | '\t' = ','): string {
  const needsQuotes = (cell: string) =>
    cell.includes(delimiter) ||
    cell.includes('"') ||
    cell.includes('\n') ||
    cell.includes('\r')

  return rows
    .map((row) =>
      row
        .map((cell) => (needsQuotes(cell) ? `"${cell.replace(/"/g, '""')}"` : cell))
        .join(delimiter),
    )
    .join('\n')
}